import AdJourneyRenderer from "./ad-journey-renderer";

export type JourneyNode = {
  id: string;
  label: string;
  type: string;
  alternative: boolean;
  connections: Set<JourneyNode>;
  fromNode: JourneyNode | null; // Where did we arrive from?
  state: JourneyNodeState;
  x: number;
  y: number;
};

type RawNode = {
  id: string;
  label: string;
  type: string;
  alternative: boolean;
  connections: string[];
};

export enum JourneyNodeState {
  UNVISITED = "UNVISITED",
  VISITED = "VISITED",
  CURRENT = "CURRENT",
}

export type AdJourneyGraph = {
  nodes: Array<JourneyNode>;
};

export class AdJourneyGuide {
  private adJourneyRenderer: AdJourneyRenderer = new AdJourneyRenderer();

  private iframe: HTMLIFrameElement | null = null;

  private bidResponse = "";

  private platform = "ios";

  private adJourneyGraph: AdJourneyGraph | null = null;

  private nodesRecord: Record<string, JourneyNode> = {};

  private currentNode: JourneyNode | null = null;

  constructor(showStateMachineCallback: () => void) {
    // Initialize our AdJourneyRenderer
    const canvas = document.getElementById(
      "ad_journey_guide_canvas"
    ) as HTMLCanvasElement;
    const container = document.getElementById(
      "preview_panel_middle"
    ) as HTMLElement;
    this.adJourneyRenderer.init(canvas, container);

    this.initializeIframeListeners(showStateMachineCallback);
  }

  public setIframe(iframe: HTMLIFrameElement): void {
    // Setup the iframe, which we'll manage in this class
    if (iframe !== this.iframe) {
      this.iframe = iframe;
      this.iframe.onload = this.iframeLoaded.bind(this);
    }
  }

  public loadIframe(
    endpoint: string,
    bidResponse: string,
    platform: string
  ): void {
    this.bidResponse = bidResponse;
    this.platform = platform;
    this.iframe?.setAttribute("src", endpoint);

    // Kick off a draw since the journey UI will be stale
    this.adJourneyGraph = null;
    this.draw();
  }

  public draw(): void {
    this.adJourneyRenderer.draw(this.adJourneyGraph);
  }

  private iframeLoaded(): void {
    console.log("ViewMaster iFrame loaded");

    // Send LOAD_AD message to the iframe to tell it to load the bid response
    this.sendMessageToIframe({
      bidResponse: this.bidResponse,
      device: this.platform === "ios" ? "iphone" : "android",
    });
  }

  private sendMessageToIframe(message: unknown): void {
    if (this.iframe && this.iframe.contentWindow) {
      this.iframe.contentWindow.postMessage(message, "*");
    }
  }

  private initializeIframeListeners(
    showStateMachineCallback: () => void
  ): void {
    window.addEventListener("message", (event) => {
      if (event.source === this.iframe?.contentWindow && event.data) {
        if (event.data.type === "SCENE_BUILDER_COMPLETE") {
          console.log("CONSTRUCTION_COMPLETE event received:", event.data.data);
          this.buildAdJourneyGraph(event.data.data);
          showStateMachineCallback();
        } else if (event.data.type === "SCENE_START") {
          console.log("SCENE_START event received:", event.data.data);
          this.transitionToNode(event.data.data.scene_id);
        }
      }
    });
  }

  private buildAdJourneyGraph(rawData: { nodes: RawNode[] }): void {
    // If we run into any unexpected nonsense here, just disable the drawing
    try {
      this.nodesRecord = {};

      this.adJourneyGraph = {
        nodes: [],
      };

      // Iterate through the nodes in the raw data first
      rawData.nodes.forEach((node: RawNode) => {
        const newNode: JourneyNode = {
          id: node.id,
          label: node.label,
          type: node.type,
          alternative: node.alternative,
          connections: new Set(),
          fromNode: null,
          state: JourneyNodeState.UNVISITED,
          x: 0,
          y: 0,
        };

        this.adJourneyGraph?.nodes.push(newNode);

        // Make record of it
        this.nodesRecord[newNode.id] = newNode;
      });

      // Now create the connections to real nodes
      this.adJourneyGraph.nodes.forEach((node: JourneyNode, i: number) => {
        const rawNode = rawData.nodes[i];
        rawNode.connections.forEach((id: string) => {
          const connection: JourneyNode = this.nodesRecord[id];
          node.connections.add(connection);
        });
      });
    } catch (error) {
      console.error(`error building journey graph: ${error}`);
      this.adJourneyGraph = null;
    }

    this.draw();
  }

  private transitionToNode(nodeId: string): void {
    const newNode: JourneyNode = this.nodesRecord[nodeId];
    if (newNode) {
      // If there is a current node, update it to be visited
      if (this.currentNode) {
        newNode.fromNode = this.currentNode;
        this.currentNode.state = JourneyNodeState.VISITED;
      }

      this.currentNode = newNode;
      this.currentNode.state = JourneyNodeState.CURRENT;

      this.draw();
    }
  }
}
